import { useState, useEffect } from 'react';
import { db, auth } from '../lib/firebase';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

export interface UserProfile {
  version?: number;
  id: string;
  name: string;
  bio: string;
  avatar: string | null;
  details?: {
    fullName?: string;
    phone?: string;
    location?: string;
  };
  settings: {
    darkMode: boolean;
    notifications: boolean;
    prayerAlerts: boolean;
    kidsMode: boolean;
  };
}

const defaultProfile: UserProfile = {
  version: 2, 
  id: '', 
  name: 'Username',
  bio: 'Seeking the pleasure of Allah.',
  avatar: null,
  details: {
    fullName: '',
    phone: '',
    location: '',
  },
  settings: {
    darkMode: false,
    notifications: true,
    prayerAlerts: true,
    kidsMode: false,
  },
};

export function useProfile() {
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);

  useEffect(() => {
    // 1. Load Local
    const saved = localStorage.getItem('habl_profile');
    let loadedProfile = defaultProfile;
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.version === 2) {
          loadedProfile = { ...defaultProfile, ...parsed };
        } else {
          loadedProfile = defaultProfile; 
        }
      } catch (e) {
        console.error('Failed to parse profile', e);
      }
    }

    if (!loadedProfile.id) {
      loadedProfile.id = crypto.randomUUID();
      localStorage.setItem('habl_profile', JSON.stringify(loadedProfile));
    }

    setProfile(loadedProfile);

    // 2. Sync with Firebase Firestore
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const docRef = doc(db, 'profiles', user.uid);
        
        // Listen to changes from Firestore
        const unsubscribeSnapshot = onSnapshot(docRef, (snapshot) => {
          if (snapshot.exists()) {
            const cloudProfile = snapshot.data() as UserProfile;
            const merged = { 
                ...defaultProfile, 
                ...cloudProfile,   
                details: cloudProfile.details || defaultProfile.details,
                version: 2 
            };

            if (user.email && (merged.name === 'Musafir' || merged.name === 'Username')) {
                 merged.name = user.email.split('@')[0];
                 setDoc(docRef, { profile: merged }, { merge: true });
            }

            setProfile(merged);
            localStorage.setItem('habl_profile', JSON.stringify(merged));
          } else {
            // First time login, create profile document
            const emailName = user.email?.split('@')[0] || 'Username';
            let newProfile = { ...loadedProfile };
            
            if (loadedProfile.name === 'Username' || loadedProfile.name === 'Musafir') {
                newProfile.name = emailName;
            }
            newProfile.id = user.uid; // Align ID with Firebase Auth UID
            
            setDoc(docRef, {
              ...newProfile,
              updated_at: serverTimestamp()
            });
            
            setProfile(newProfile);
            localStorage.setItem('habl_profile', JSON.stringify(newProfile));
          }
        });
        
        return () => unsubscribeSnapshot();
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const saveProfile = async (updated: UserProfile) => {
    setProfile(updated);
    localStorage.setItem('habl_profile', JSON.stringify(updated));
    
    // Push to cloud if logged in
    const user = auth.currentUser;
    if (user) {
       const docRef = doc(db, 'profiles', user.uid);
       setDoc(docRef, {
           ...updated,
           updated_at: serverTimestamp()
       }, { merge: true }).catch((error) => {
           console.warn('Public profile sync failed:', error);
       });
    }
  };

  const updateProfile = (updates: Partial<UserProfile>) => {
    setProfile(prev => {
      let newDetails = prev.details;
      if (updates.details) {
          newDetails = { ...prev.details, ...updates.details };
      }

      const updated = { 
          ...prev, 
          ...updates,
          details: newDetails 
      };
      saveProfile(updated);
      return updated;
    });
  };

  const updateSettings = (settingsUpdates: Partial<UserProfile['settings']>) => {
    setProfile(prev => {
      const updated = { 
        ...prev, 
        settings: { ...prev.settings, ...settingsUpdates } 
      };
      saveProfile(updated);
      return updated;
    });
  };

  return { profile, updateProfile, updateSettings };
}
